<template>
  <!--
    Interactive disclosure toggle: a dedicated control so a branch row can be
    selected by clicking its body while this button (only) expands / collapses
    the row's children. `@click.stop` keeps the click from reaching sp-list's
    row-level handler, so toggling never selects. Keyboard expansion stays on the
    row (arrow keys, handled upstream), so this is removed from the tab order.
  -->
  <button
    v-if="interactive"
    type="button"
    tabindex="-1"
    class="sp-list-item__append-toggle"
    :aria-label="label"
    :data-testid="testId"
    :aria-expanded="ariaExpanded"
    @click.stop="emit('append-action')"
  >
    <sp-icon v-if="showDisclosureIcon" :name="disclosureIconName" class="sp-list-item__append-icon"></sp-icon>
    <sp-caret
      v-else
      class="sp-list-item__append-caret"
      variant="chevron"
      :expand-direction="caretExpandDirection"
      :active="expanded"
      color="var(--list-item-append-icon-fill)"
    ></sp-caret>
  </button>
  <sp-icon v-else-if="showDisclosureIcon" :name="disclosureIconName" class="sp-list-item__append-icon"></sp-icon>
  <sp-caret
    v-else
    class="sp-list-item__append-caret"
    variant="chevron"
    :expand-direction="caretExpandDirection"
    :active="expanded"
    color="var(--list-item-append-icon-fill)"
  ></sp-caret>
</template>

<script setup>
import { computed } from "vue";

const emit = defineEmits(["append-action"]);

const props = defineProps({
  interactive: Boolean,
  showDisclosureIcon: Boolean,
  disclosureIconName: { type: String, default: undefined },
  expanded: Boolean,
  // In-place toggle rows point the chevron down (collapsed) and up (expanded);
  // without it the caret is a drill-in affordance pointing right, down when active.
  toggle: Boolean,
  label: { type: String, default: undefined },
  testId: { type: String, default: undefined },
});

const ariaExpanded = computed(() => (props.expanded ? "true" : "false"));

const caretExpandDirection = computed(() => (props.toggle ? undefined : "down"));
</script>

<style scoped lang="scss">
// The `--list-item-*` custom props are set on the SpListItem host and inherit
// through the shadow DOM into this child.
.sp-list-item__append-icon {
  --fill: var(--list-item-append-icon-fill);
  --size: var(--list-item-icon-size);
}

// Align the append caret with the static `sp-icon` append (and the prepend
// icon). SpCaret's host defaults — both needed for its inline usages (e.g.
// text-field caret) — fight this layout here:
//   • `display: inline-block` sits on the block's text baseline and adds the
//     line box's descent (~3.5px at 16px) below the 2rem glyph, inflating the
//     row height. `display: block` makes it occupy only its own box.
//   • `transform: translateY(-0.125rem)` nudges it 2px up for inline contexts,
//     which here pushes it 2px above the vertically-centred prepend icon.
.sp-list-item__append-caret {
  display: block;
  transform: none;
  // SpCaret's active flip carries a translateY nudge for inline contexts that
  // would push the flipped glyph 0.25rem below the row's centre; zero it so the
  // caret stays box-centred in both states.
  --sp-comp-caret-active-nudge: 0;
  // Scale the caret with the row's icon size. SpCaret's --sp-comp-caret-size
  // drives its inner sp-icon's size in both dimensions, so the glyph scales
  // uniformly.
  --sp-comp-caret-size: var(--list-item-icon-size);
}

// Bare button wrapper for the interactive disclosure toggle: no chrome, just a
// pointer hit area around the caret.
.sp-list-item__append-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
}
</style>
